import { Box, Stack } from "@repo/ui/core";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";

// ─── Node Data ─────────────────────────────────────────────────────────────────
// Phase names and colors are intentionally identical to GanttSlide so the
// audience can recognise each phase when the Gantt chart appears on the
// next slide.

type VNode = {
  id: string;
  label: [string, string];
  color: string;
  cx: number;
  cy: number;
};

const NODES: VNode[] = [
  { id: "concept",     label: ["Concept &",  "Requirements"], color: "#4ade80", cx: 90,  cy: 105 },
  { id: "system-arch", label: ["System",     "Architecture"], color: "#2dd4bf", cx: 213, cy: 188 },
  { id: "hw-sw-def",   label: ["HW/SW",      "Definition"],   color: "#22d3ee", cx: 337, cy: 270 },
  { id: "sw-dev",      label: ["SW",         "Development"],  color: "#60a5fa", cx: 450, cy: 318 },
  { id: "hw-sw-integ", label: ["HW/SW",      "Integration"],  color: "#818cf8", cx: 563, cy: 270 },
  { id: "validation",  label: ["System",     "Validation"],   color: "#a78bfa", cx: 687, cy: 188 },
  { id: "sop",         label: ["SOP",        "Preparation"],  color: "#c084fc", cx: 810, cy: 105 },
];

const R = 26;       // node circle radius
const FS = 11;      // label font size
const LH = 16;      // label line height

// Paired verification/validation connector lines (horizontal dashes)
const CONNECTORS: [number, number][] = [
  [0, 6],
  [1, 5],
  [2, 4],
];

// The V-line path
const V_PATH = NODES.map((n, i) => `${i === 0 ? "M" : "L"} ${n.cx},${n.cy}`).join(" ");

// ─── Label Position Helper ──────────────────────────────────────────────────────

function labelAnchorX(node: VNode, idx: number): { anchor: "start" | "middle" | "end"; x: number } {
  if (idx === 0 || idx === 3 || idx === 6) return { anchor: "middle", x: node.cx };
  if (idx < 3)  return { anchor: "end",   x: node.cx - R - 8 };
  return              { anchor: "start",  x: node.cx + R + 8 };
}

function labelY1(node: VNode, idx: number): number {
  if (idx === 0 || idx === 6) return node.cy - R - 22; // above top nodes
  if (idx === 3)              return node.cy + R + 14; // below bottom node
  return node.cy - LH / 2;                             // vertically centred on side nodes
}

// ─── Slide ─────────────────────────────────────────────────────────────────────

export function VModelSlide() {
  return (
    <Box w="100%" maw={1400} px="xl">
      <Stack gap="md">
        {/* ── Title ── */}
        <SlideTitle
          title="The Automotive V-Model"
          subtitle="The industry-standard framework behind every major OEM development program"
        />

        {/* ── Diagram ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <svg
            viewBox="0 0 900 400"
            width="100%"
            style={{ display: "block", maxHeight: "62vh" }}
            aria-label="Automotive V-Model diagram showing the seven development phases"
          >
            <defs>
              <filter id="vmodel-glow" x="-80%" y="-80%" width="260%" height="260%">
                <feGaussianBlur stdDeviation="7" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* ── Arm labels ── */}
            <motion.text
              x={230}
              y={56}
              textAnchor="middle"
              fontSize={10}
              fill="rgba(255,255,255,0.18)"
              fontStyle="italic"
              fontFamily="inherit"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 3.0, duration: 0.5 }}
            >
              Design &amp; Definition →
            </motion.text>
            <motion.text
              x={670}
              y={56}
              textAnchor="middle"
              fontSize={10}
              fill="rgba(255,255,255,0.18)"
              fontStyle="italic"
              fontFamily="inherit"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 3.0, duration: 0.5 }}
            >
              ← Verify &amp; Validate
            </motion.text>

            {/* ── Connector lines (dashed, horizontal) ── */}
            {CONNECTORS.map(([fi, ti], i) => {
              const from = NODES[fi] as VNode;
              const to   = NODES[ti] as VNode;
              return (
                <motion.path
                  key={`conn-${i}`}
                  d={`M ${from.cx + R},${from.cy} L ${to.cx - R},${to.cy}`}
                  stroke="rgba(255,255,255,0.08)"
                  strokeWidth={1}
                  strokeDasharray="5 4"
                  fill="none"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ delay: 2.5 + i * 0.18, duration: 0.55 }}
                />
              );
            })}

            {/* ── V-line ── */}
            <motion.path
              d={V_PATH}
              stroke="rgba(255,255,255,0.14)"
              strokeWidth={2}
              fill="none"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.45, duration: 1.5, ease: "easeInOut" }}
            />

            {/* ── Nodes ── */}
            {NODES.map((node, i) => {
              const { anchor, x: lx } = labelAnchorX(node, i);
              const ly1 = labelY1(node, i);
              const ly2 = ly1 + LH;

              return (
                <motion.g
                  key={node.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9 + i * 0.13, duration: 0.4 }}
                >
                  {/* Soft glow behind circle */}
                  <circle
                    cx={node.cx}
                    cy={node.cy}
                    r={R + 10}
                    fill={node.color}
                    opacity={0.1}
                  />

                  {/* Outer ring */}
                  <circle
                    cx={node.cx}
                    cy={node.cy}
                    r={R}
                    fill="rgba(10,10,14,0.7)"
                    stroke={node.color}
                    strokeWidth={2}
                  />

                  {/* Inner colour fill */}
                  <circle
                    cx={node.cx}
                    cy={node.cy}
                    r={12}
                    fill={node.color}
                    opacity={0.3}
                  />

                  {/* Two-line label */}
                  <text
                    x={lx}
                    y={ly1}
                    textAnchor={anchor}
                    fontSize={FS}
                    fill={node.color}
                    fontWeight={600}
                    fontFamily="inherit"
                    style={{ letterSpacing: "0.01em" }}
                  >
                    {node.label[0]}
                  </text>
                  <text
                    x={lx}
                    y={ly2}
                    textAnchor={anchor}
                    fontSize={FS}
                    fill={node.color}
                    fontWeight={600}
                    fontFamily="inherit"
                    style={{ letterSpacing: "0.01em" }}
                  >
                    {node.label[1]}
                  </text>
                </motion.g>
              );
            })}
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
